// eslint-disable-next-line no-restricted-imports
import { DeltaOperation, DeltaStatic } from 'quill';
import {
  AI_EMAIL_LINK_FORMAT,
  AI_EMAIL_PILL_TEXT_MAX_LENGTH,
  AI_EMAIL_PLACEHOLDER_FORMAT,
  AI_EMAIL_SENDER_FORMAT,
} from './ai-email-editor.constants';

type PillRun = { text: string };

export type BodyStats = {
  placeholders: Map<string, number>;
  linkRuns: PillRun[];
  senderRuns: PillRun[];
  bodyText: string;
};

const collectRuns = (ops: DeltaOperation[], format: string): PillRun[] => {
  const runs: PillRun[] = [];
  let current: PillRun | null = null;

  ops.forEach((op) => {
    if (!op.attributes?.[format]) {
      current = null;
      return;
    }
    if (!current) {
      current = { text: '' };
      runs.push(current);
    }
    if (typeof op.insert === 'string') {
      current.text += op.insert;
    }
  });

  return runs;
};

export const getBodyStats = (contents: DeltaStatic): BodyStats => {
  const ops = contents.ops ?? [];
  const placeholders = new Map<string, number>();
  let bodyText = '';

  ops.forEach((op) => {
    if (typeof op.insert === 'string') {
      if (
        !op.attributes?.[AI_EMAIL_LINK_FORMAT] &&
        !op.attributes?.[AI_EMAIL_SENDER_FORMAT]
      ) {
        bodyText += op.insert;
      }
      return;
    }
    const placeholder = op.insert?.[AI_EMAIL_PLACEHOLDER_FORMAT];
    if (placeholder !== undefined) {
      placeholders.set(placeholder, (placeholders.get(placeholder) ?? 0) + 1);
    }
  });

  return {
    placeholders,
    linkRuns: collectRuns(ops, AI_EMAIL_LINK_FORMAT),
    senderRuns: collectRuns(ops, AI_EMAIL_SENDER_FORMAT),
    bodyText,
  };
};

const havePlaceholdersChanged = (
  next: Map<string, number>,
  previous: Map<string, number>,
) =>
  next.size !== previous.size ||
  Array.from(next).some(([value, count]) => previous.get(value) !== count);

const hasRunGrownTooLong = (next: PillRun[], previous: PillRun[]) =>
  next.some(
    (run, index) =>
      run.text.length > AI_EMAIL_PILL_TEXT_MAX_LENGTH &&
      run.text.length > previous[index].text.length,
  );

export const isChangeRejected = (next: BodyStats, previous: BodyStats) =>
  havePlaceholdersChanged(next.placeholders, previous.placeholders) ||
  next.linkRuns.length !== previous.linkRuns.length ||
  next.senderRuns.length !== previous.senderRuns.length ||
  hasRunGrownTooLong(next.linkRuns, previous.linkRuns) ||
  hasRunGrownTooLong(next.senderRuns, previous.senderRuns);
